import React, { useEffect, useState } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.min.css';
import template from '../assets/dailyReportTemplate.json';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { db } from '../firebaseConfig.js';
import { collection, doc, setDoc, addDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import FullScreenButton from './FullScreenButton';

registerAllModules();

interface TableRow {
  id: string;
  rowId: string;
  order: number;
  company: string;
  vehicleNumber: string;
  route: string;
  itemName: string;
  cargo: string;
  billingAmount: string | number;
  department: string;
  contact: string;
  paymentAmount: string | number;
  businessNumber: string;
  driverName: string;
  distance: string | number;
  weight: string | number;
  remarks: string;
  memo: string;
  profit: string | number;
  settlementStatus: string;
  settlementDate: string | null;
  Receipt: string;
  createdAt: string;
  updatedAt: string;
  date: string;
  customerMessage?: string;
}

interface CreateDailyReportProps {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
}

export const CenterContent = ({ startDate, setStartDate, endDate, setEndDate, handleCreate }: any) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
    <DatePicker
      selected={startDate ?? undefined}
      onChange={(date: Date | null) => setStartDate(date)}
      selectsStart
      startDate={startDate ?? undefined}
      endDate={endDate ?? undefined}
      dateFormat="yyyy-MM-dd"
      placeholderText="시작일 선택"
      popperClassName="custom-datepicker-popper"
    />
    <span style={{ margin: '0 4px' }}>~</span>
    <DatePicker
      selected={endDate ?? undefined}
      onChange={(date: Date | null) => setEndDate(date)}
      selectsEnd
      startDate={startDate ?? undefined}
      endDate={endDate ?? undefined}
      minDate={startDate ?? undefined}
      dateFormat="yyyy-MM-dd"
      placeholderText="종료일 선택"
      popperClassName="custom-datepicker-popper"
    />
    <button onClick={handleCreate} style={{ marginLeft: 8, padding: '6px 18px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>생성</button>
  </div>
);

function formatDateToYYYYMMDD(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const CreateDailyReport: React.FC<CreateDailyReportProps> = ({ isFullScreen, setIsFullScreen }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const data = (template as any).data;
  const mergeCells = (template as any).mergeCells;
  const rowColors = (template as any).rowColors;

  const parseTableData = () => {
    const rows: TableRow[] = [];
    let lastCompany = '';
    // 전역 순서값 (고유하고 일관된 정렬 보장을 위해 1000 단위 증가)
    let globalOrder = 1000;
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (row[0] && row[0].trim() !== '') {
        lastCompany = row[0];
      }
      rows.push({
        id: '',
        rowId: '',
        order: globalOrder,
        company: lastCompany,
        vehicleNumber: row[1],
        route: row[2],
        itemName: row[3],
        cargo: row[4],
        billingAmount: row[5],
        department: row[6],
        contact: row[7],
        paymentAmount: row[8],
        businessNumber: row[9],
        driverName: row[10],
        distance: row[11],
        weight: row[12],
        remarks: row[13],
        memo: row[14] || '',
        profit: '',
        settlementStatus: '미정산',
        settlementDate: null,
        Receipt: '미수취',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        date: '',
        customerMessage: '',
      });
      globalOrder += 1000;
    }
    return rows;
  };

  const saveToFirestore = async (dateStr: string) => {
    try {
      const reportDocRef = doc(collection(db, 'dispatchReports'), dateStr);
      await setDoc(reportDocRef, {
        date: dateStr,
        exportramenNum: '',
        exportramenMemo: '',
        domesticramenNum: '',
        domesticramenMemo: '',
        oilramenNum: '',
        oilramenMemo: '',
        totalRemark1: '',
        totalRemark2: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const rowsColRef = collection(reportDocRef, 'rows');
      const rows = parseTableData();
      
      for (let i = 0; i < rows.length; i++) {
        const timestamp = Date.now();
        const rowId = `${dateStr}_${timestamp}_${i}`;
        
        const rowData: TableRow = {
          ...rows[i],
          id: rowId,
          rowId: rowId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          date: dateStr,
        };

        const rowDocRef = doc(rowsColRef, rowId);
        await setDoc(rowDocRef, rowData);
      }

      alert('배차일보가 성공적으로 저장되었습니다!');
    } catch (error) {
      alert('저장 중 오류가 발생했습니다: ' + error);
    }
  };

  const handleCreate = async () => {
    if (!startDate || !endDate) {
      alert('시작일과 종료일을 모두 선택하세요.');
      return;
    }
    const dates: string[] = [];
    let d = new Date(startDate);
    while (d <= endDate) {
      dates.push(formatDateToYYYYMMDD(d));
      d.setDate(d.getDate() + 1);
    }
    // 중복 체크: 모든 날짜에 대해 dispatchReports 문서 존재 여부 확인
    let hasDuplicate = false;
    for (const dateStr of dates) {
      const reportDocRef = doc(collection(db, 'dispatchReports'), dateStr);
      const reportSnap = await getDoc(reportDocRef);
      if (reportSnap.exists()) {
        hasDuplicate = true;
        break;
      }
    }
    if (hasDuplicate) {
      alert('생성된 배차일보가 존재합니다. 날짜를 확인하세요.');
      return;
    }
    for (const dateStr of dates) {
      await saveToFirestore(dateStr);
    }
  };

  const cells = (row: number, col: number) => {
    const cellProps: any = {};
    let classNames: string[] = [];
    if (rowColors && rowColors[row]) {
      classNames.push('custom-row-color-' + row);
    }
    if (row > 0 && data[row][0] && data[row][0].trim().length > 0) {
      classNames.push('custom-company-row');
    }
    if (classNames.length > 0) {
      cellProps.className = classNames.join(' ');
    }
    cellProps.className = (cellProps.className || '') + ' htCenter htMiddle';
    return cellProps;
  };

  useEffect(() => {
    if (!document.getElementById('row-title-style')) {
      const style = document.createElement('style');
      style.id = 'row-title-style';
      style.innerHTML = `.custom-row-title { background: #e3f2fd !important; }`;
      document.head.appendChild(style);
    }
    if (!document.getElementById('company-row-style')) {
      const style = document.createElement('style');
      style.id = 'company-row-style';
      style.innerHTML = `.custom-company-row { background: #e3f2fd !important; }`;
      document.head.appendChild(style);
    }
    if (rowColors) {
      Object.entries(rowColors).forEach(([rowIdx, color]) => {
        const styleId = `row-color-style-${rowIdx}`;
        if (!document.getElementById(styleId)) {
          const style = document.createElement('style');
          style.id = styleId;
          style.innerHTML = `.custom-row-color-${rowIdx} { background: ${color} !important; }`;
          document.head.appendChild(style);
        }
      });
    }
  }, [rowColors]);

  const colWidths = [150, 150, 150, 150, 150, 150, 150, 150, 150, 150, 150, 150, 150, 200];

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
      <CenterContent
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        handleCreate={handleCreate}
      />
      <HotTable
        data={data}
        mergeCells={mergeCells}
        colHeaders={false}
        rowHeaders={true}
        stretchH="all"
        width="100%"
        height="100%"
        colWidths={colWidths}
        cells={cells}
        fixedRowsTop={1}
        fixedColumnsLeft={1}
        manualRowResize={true}
        manualColumnResize={true}
        copyPaste={true}
        className="htCenter"
        licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
      />
    </div>
  );
};

export default CreateDailyReport;
 